import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';

// Import custom components
import { OpTable, IOpTableProps } from 'components/customAntd/DLS/OpTable/OpTable';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
// Import actions and types from Redux slices
import { AppDispatch, RootState } from 'store/store';

// Import constants
import { DATE_FORMAT } from 'constants/dates';
import { TABLE_HEIGHT } from 'constants/ui';
import { formatFullName } from 'utils/utils';
import { Visitor } from 'types/visitTypes';
import { setSelectedVisit } from 'store/slices/visitsSlice';
import { useTranslation } from 'react-i18next';

interface DeniedVistiorsTableData {
    visitId: number;
    visitorId: number;
    fullName: string;
    date: string;
}

interface ExtendedVisitor extends Visitor {
    visitId: number;
};

interface DeniedVisitorsTableProps {
    deniedVisitors: ExtendedVisitor[];
    setDrawerVisible: (visible: boolean) => void;
    loading: boolean;
}

const DeniedVisitorsTable: React.FC<DeniedVisitorsTableProps> = ({ deniedVisitors, setDrawerVisible, loading }) => {
    const dispatch: AppDispatch = useDispatch();

    const { visits } = useSelector((state: RootState) => state.visits);

    const { t } = useTranslation();

    // Format visitors for table display
    const formatVisitsForTable = (deniedVisitors: ExtendedVisitor[]): DeniedVistiorsTableData[] => {
        return deniedVisitors
            .map((deniedVisitors) => {
                const date = dayjs(deniedVisitors.signIn).format(DATE_FORMAT);
                return {
                    visitId: deniedVisitors.visitId!,
                    visitorId: deniedVisitors.id!,
                    fullName: formatFullName(deniedVisitors.firstName, deniedVisitors.middleName, deniedVisitors.lastName),
                    date: date
                };
            });
    };

    // Table columns definition
    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'fullName',
            label: t('redFlagReport.table.fullName'),
            filter: { type: 'input' },
            sorter: (a, b) => a.fullName.localeCompare(b.fullName), // Alphabetical sorting
        },
        {
            dataIndex: 'date',
            label: t('redFlagReport.table.date'),
            sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
        }
    ];

    // Table properties
    const opTableProps: IOpTableProps = {
        dataSource: formatVisitsForTable(deniedVisitors),
        label: t('redFlagReport.table.denied'),
        columns: columns,
        rowActions: {
            onEditClick: (visitor: DeniedVistiorsTableData) => {
                const selectedVisit = visits.find(v => Number(v.id) === Number(visitor.visitId));
                dispatch(setSelectedVisit(selectedVisit!));
                setDrawerVisible(true);
            },
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: true,
        allowExport: true,
        allowShowHideColumns: true,
        loading: loading,
        rowKey: (record) => `${record.visitId}-${record.visitorId}`,
    };

    return <OpTable {...opTableProps} />;
}

export default DeniedVisitorsTable;
